// Copyright ©️ 2026 eVolve MEP, LLC

import { formatDate, today } from '@evolvemep/api-ts';
import { Stack } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { CustomPropertiesDetailSection } from 'components/Mantine/CustomProperties/CustomPropertiesDetailSection';
import { DatePickerWrapped } from 'components/Mantine/DateInputWrapped';
import { DetailSection } from 'components/Mantine/DetailPage/DetailSection';
import { SmallDetailItem } from 'components/Mantine/DetailPage/SmallDetailItem';
import { convertSecondsToDuration } from 'components/Mantine/durationHelpers';
import { DurationInput } from 'components/Mantine/DurationInput';
import { WORK_ORDER_LABEL_TYPE_ID } from 'components/Mantine/Labels/constants';
import { LabelBadges } from 'components/Mantine/Labels/LabelBadges';
import { ManageLabelsModal } from 'components/Mantine/Labels/ManageLabelsModal/ManageLabelsModal';
import { MultiUserSelect } from 'components/Mantine/MultiUserSelect';
import { TextInputWrapped } from 'components/Mantine/TextInputWrapped';
import { UserInfoBadge } from 'components/Mantine/UserInfoBadge';
import { EditableCell } from 'helpers/ag-grid/EditableCells/EditableCell';
import { useHasAccess } from 'hooks/user/useHasAccess';
import { EditableWorkOrderStatusBadge } from 'modules/Shop/WorkOrders/WorkOrderDetail/EditableWorkOrderStatusBadge';
import { FacilityListSelector } from 'modules/Shop/WorkOrders/WorkOrderDetail/FacilityListSelector';
import type { useWorkOrderDetailMutations } from 'modules/Shop/WorkOrders/WorkOrderDetail/useWorkOrderDetailMutations';
import { WorkOrderShipmentsSection } from 'modules/Shop/WorkOrders/WorkOrderDetail/WorkOrderShipmentsSection';
import { getDaysRemainingOrDaysEarlyLate } from 'modules/Shop/WorkOrders/WorkOrdersPage/common';
import { WorkOrdersLabelChecklist } from 'modules/Shop/WorkOrders/WorkOrdersPage/WorkOrdersLabelChecklist';

/**
 * The Work Order detail "side pane" — status, dates, assignees, labor, custom properties, labels,
 * shipments, and properties. Shared between the desktop and mobile Work Order detail pages; the
 * caller controls sizing/placement (e.g. a fixed-width column on desktop, full-width on mobile).
 */
export const WorkOrderDetailSidePane = ({
  workOrder,
  updateWorkOrder,
  refreshWorkOrder,
  editable,
  saving,
  canEditWorkOrder,
}: ReturnType<typeof useWorkOrderDetailMutations>) => {
  const hasChangeWorkOrderAccess = useHasAccess('workOrders', 'write');

  return (
    <Stack>
      <DetailSection title="Status">
        <EditableWorkOrderStatusBadge
          workOrder={workOrder}
          editable
          onWorkOrderUpdated={refreshWorkOrder}
          size="xxl"
          fullWidth
        />
      </DetailSection>

      <DetailSection title="Dates">
        <EditableCell
          label="Need By"
          labelTooltip="The requested date which the order must be completed by."
          Editor={DatePickerWrapped}
          editorProps={{ minDate: today() }}
          initialValue={workOrder.needByDate}
          onSave={async (date) => updateWorkOrder({ needByDate: date })}
          renderValue={
            <span
              style={{
                color:
                  getDaysRemainingOrDaysEarlyLate(workOrder.needByDate, workOrder.productionCompletedOn) < 0
                    ? 'red'
                    : undefined,
              }}
            >
              {formatDate(workOrder.needByDate, 'MM/dd/yyyy')}
            </span>
          }
          closeOnChange
          editable={editable}
        />
        <EditableCell
          label="On-Site Date"
          labelTooltip="The date which has been coordinated as the actual delivery date."
          Editor={DatePickerWrapped}
          initialValue={workOrder.onSiteDate}
          onSave={async (date) => updateWorkOrder({ onSiteDate: date })}
          renderValue={formatDate(workOrder.onSiteDate, 'MM/dd/yyyy')}
          closeOnChange
          // On-site date has its own "editable" logic
          editable={!saving && canEditWorkOrder('editOnSiteDate')}
        />
        <SmallDetailItem
          label="Production Start"
          tooltip="Identifies the start date of the first task within the work order."
        >
          {formatDate(workOrder.productionStartedOn, 'MM/dd/yyyy')}
        </SmallDetailItem>
        <SmallDetailItem
          label="Production Complete"
          tooltip="Identifies the completion date of the last task within the work order."
        >
          {formatDate(workOrder.productionCompletedOn, 'MM/dd/yyyy')}
        </SmallDetailItem>
      </DetailSection>

      <DetailSection
        title="Assignees"
        rightSection={
          <EditableCell
            Editor={MultiUserSelect}
            initialValue={workOrder.assignees}
            renderIcon="Add"
            onSave={(assignees) =>
              updateWorkOrder({
                assignees: assignees?.map((a) => a.userId) ?? [],
              })
            }
            editable={editable}
          />
        }
      >
        <EditableCell
          Editor={MultiUserSelect}
          initialValue={workOrder.assignees}
          renderValue={
            <Stack gap="xs">
              {workOrder.assignees.map((assignee) => (
                <UserInfoBadge key={assignee.userId} user={assignee} />
              ))}
            </Stack>
          }
          emptyState={
            <>
              <EvolveIcon icon="EmptyCircle" size="sm" />
              Add assignee
            </>
          }
          showEmptyState={workOrder.assignees.length === 0}
          onSave={(assignees) =>
            updateWorkOrder({
              assignees: assignees?.map((a) => a.userId) ?? [],
            })
          }
          editable={editable}
        />
      </DetailSection>

      {hasChangeWorkOrderAccess && (
        <DetailSection title="Labor">
          <EditableCell
            label="Estimated Time"
            Editor={DurationInput}
            editorProps={{
              defaultValue: workOrder.estimatedTimeInSeconds,
            }}
            initialValue={workOrder.estimatedTimeInSeconds}
            editable={editable}
            onSave={(estimatedTimeInSeconds) => updateWorkOrder({ estimatedTimeInSeconds })}
            renderValue={convertSecondsToDuration(workOrder.estimatedTimeInSeconds)}
          />
          <SmallDetailItem label="Total Time" tooltip="The sum of all time for all tasks within the work order.">
            {workOrder.totalTimeInSeconds ? convertSecondsToDuration(workOrder.totalTimeInSeconds) : '--'}
          </SmallDetailItem>
        </DetailSection>
      )}

      <CustomPropertiesDetailSection
        disabled={saving}
        onUpdateCustomProperty={(customPropertyId, value) =>
          updateWorkOrder({
            customProperties: [{ customPropertyId, value }],
          })
        }
        entity={workOrder}
        customPropertyTypeName="Work Order"
      />

      <DetailSection
        title="Labels"
        rightSection={
          <EditableCell
            Editor={WorkOrdersLabelChecklist}
            initialValue={workOrder.labels}
            renderIcon="Add"
            onSave={(labels) => updateWorkOrder({ labels: labels?.map((label) => label.labelId) ?? [] })}
            footer={<ManageLabelsModal labelTypeId={WORK_ORDER_LABEL_TYPE_ID} />}
            editable={editable}
          />
        }
      >
        <EditableCell
          Editor={WorkOrdersLabelChecklist}
          initialValue={workOrder.labels}
          renderValue={
            <LabelBadges labelTypeId={WORK_ORDER_LABEL_TYPE_ID} labels={workOrder.labels} truncate={false} />
          }
          emptyState={
            <>
              <EvolveIcon icon="Label" size="sm" />
              Add label
            </>
          }
          showEmptyState={workOrder.labels.length === 0}
          onSave={(labels) => updateWorkOrder({ labels: labels?.map((label) => label.labelId) ?? [] })}
          footer={<ManageLabelsModal labelTypeId={WORK_ORDER_LABEL_TYPE_ID} />}
          editable={editable}
        />
      </DetailSection>

      <WorkOrderShipmentsSection workOrderId={workOrder.workOrderId} />

      <DetailSection title="Properties">
        <EditableCell
          label="Work Order Name"
          Editor={TextInputWrapped}
          initialValue={workOrder.name}
          editable={editable}
          onSave={(name) => updateWorkOrder({ name })}
          renderValue={workOrder.name}
        />
        <EditableCell
          label="Work Order Id"
          Editor={TextInputWrapped}
          initialValue={workOrder.identifier}
          // Limited Access users cannot adjust the WO ID
          editable={editable && canEditWorkOrder('editWorkOrderId')}
          onSave={(identifier) => updateWorkOrder({ identifier })}
          renderValue={workOrder.identifier}
        />
        <EditableCell
          label="Facility"
          Editor={FacilityListSelector}
          initialValue={{
            facilityName: workOrder.facilityName,
            facilityId: workOrder.facilityId,
          }}
          renderValue={workOrder.facilityName}
          editable={editable && canEditWorkOrder('editFacility')}
          onSave={(facility) => updateWorkOrder({ facilityId: facility?.facilityId })}
        />
        <SmallDetailItem label="Project">{workOrder.projectName}</SmallDetailItem>
        <SmallDetailItem label="Created By">
          <UserInfoBadge user={workOrder.createdByUser} />
        </SmallDetailItem>
      </DetailSection>
    </Stack>
  );
};
